import { Button, Form, Grid, Segment } from '@mairistem/composants';
import { request } from '@mairistem/tools';
import _ from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';
import { ENV_LOCAL } from '@/constants';
import { CODE_REGIE_LOOC } from '@/constants/applicationSource';
import { RegieContext } from '@/contexts';
import {
  baseUrl,
  createFactures,
  deleteFactures,
  deleteRecus,
  incorporationFlux,
  incorporationFluxContrat,
  putRegie,
  updateCycles,
} from '@/routes';
import { useParametreStore } from '@/zustand';
import { InputNumber } from '../Autre';
import ParametrageQualiteSuppressionRegie from './ParametrageQualiteSuppressionRegie';

const ParametrageQualite = ({ full = false }: { full?: boolean }) => {
  const [loading, setLoading] = React.useState<boolean>(false);
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [idFactureToEncaisseDirect, setIdFactureToEncaisseDirect] = React.useState<number | null>(
    null,
  );

  const { regies, selectedRegie } = React.useContext(RegieContext);
  const regie = regies?.find((r) => r.identifiant === selectedRegie);

  const setParametre = useParametreStore((state) => state.setParametre);

  const handleDeleteRecus = React.useCallback(() => {
    setLoading(true);

    request
      .delete(deleteRecus())
      .then(() => {
        toast('Les reçus ont bien été supprimés', { type: 'success' });
      })
      .catch(() => {
        toast('Une erreur est survenue lors de la suppression des reçus', { type: 'error' });
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleDeleteFactures = React.useCallback(() => {
    setLoading(true);
    request
      .delete(deleteRecus())
      .then(() => {
        request.delete(deleteFactures()).then(() => {
          toast('Les factures ont bien été supprimés', { type: 'success' });
        });
      })
      .catch(() => {
        toast('Une erreur est survenue lors de la suppression des factures', { type: 'error' });
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleCreateFactures = React.useCallback(() => {
    setLoading(true);

    request
      .post(createFactures())
      .then(() => {
        toast('Les factures ont bien été créées', { type: 'success' });
      })
      .catch(() => {
        toast('Une erreur est survenue lors de la création des factures', { type: 'error' });
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleUpdateCycle = React.useCallback(() => {
    setLoading(true);

    request
      .put(updateCycles())
      .then(() => {
        toast('Les cycles de facturation ont bien été mis à jour', { type: 'success' });
      })
      .catch(() => {
        toast('Une erreur est survenue lors de la mise à jour des cycles de facturation', {
          type: 'error',
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleRegieEcole = React.useCallback(() => {
    setLoading(true);

    request
      .put(putRegie(regie.identifiant), { ...regie, ecole: true })
      .then(() => {
        toast('La régie est redevenue une régie test', { type: 'success' });
      })
      .catch(() => {
        toast('Une erreur est survenue lors de la mise à jour de la régie', { type: 'error' });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [regie]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const handlePayeurDelete = React.useCallback(() => {
    // Met une variable globale à true pour activer la suppression des payeurs
    setParametre({ suppressionPayeur: true });
  }, [setParametre]);

  const handleImportFlux = React.useCallback(() => {
    if (!selectedFile) {
      toast('Veuillez sélectionner un fichier', { type: 'warning' });
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append('file', selectedFile);

    request
      .post(incorporationFlux(), formData)
      .then(() => {
        toast('Le flux a bien été importé', { type: 'success' });
      })
      .catch((error) => {
        if (typeof error?.message?.message === 'string') {
          toast(error.message.message, { type: 'error' });
          return;
        }

        // Si le message est un objet, on affiche les erreurs
        // Récupération de la liste des erreurs
        if (error?.message?.errors) {
          const errorMessages = Object.entries(error?.message?.errors).flatMap(
            ([key, messages]) => {
              const errorList = Array.isArray(messages) ? messages : [messages];
              return errorList.map((message) => `${key}: ${message}`);
            },
          );
          toast(
            <div>
              {errorMessages.map((errorLine, idx) => (
                <div key={`${idx}-${errorLine}`}>{errorLine}</div>
              ))}
            </div>,
            { type: 'error' },
          );
        }
      })
      .finally(() => {
        setSelectedFile(null);
        setLoading(false);
      });
  }, [selectedFile]);

  const handleImportFluxContrat = React.useCallback(() => {
    if (!selectedFile) {
      toast('Veuillez sélectionner un fichier', { type: 'warning' });
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append('file', selectedFile);

    request
      .post(incorporationFluxContrat(), formData)
      .then(() => {
        toast('Le flux de contrats a bien été importé', { type: 'success' });
      })
      .catch((error) => {
        if (typeof error?.message?.message === 'string') {
          toast(error.message.message, { type: 'error' });
          return;
        }

        // Si le message est un objet, on affiche les erreurs
        // Récupération de la liste des erreurs
        if (error?.message?.errors) {
          const errorMessages = Object.entries(error?.message?.errors).flatMap(
            ([key, messages]) => {
              const errorList = Array.isArray(messages) ? messages : [messages];
              return errorList.map((message) => `${key}: ${message}`);
            },
          );
          toast(
            <div>
              {errorMessages.map((errorLine, idx) => (
                <div key={`${idx}-${errorLine}`}>{errorLine}</div>
              ))}
            </div>,
            { type: 'error' },
          );
        }
      })
      .finally(() => {
        setSelectedFile(null);
        setLoading(false);
      });
  }, [selectedFile]);

  const handleEncaissementDirect = React.useCallback(() => {
    setLoading(true);

    request
      .post(
        `${baseUrl}/generation-app-url`,
        {
          cleLiaison: _.find(regie.logicielsFacturier, ({ code }) => code === CODE_REGIE_LOOC)
            ?.cleLiaison,
          identifiantFacture: idFactureToEncaisseDirect,
        },
        ENV_LOCAL
          ? {
              headers: {
                'X-Database':
                  'eyJpdiI6InRiaEF1SEUxK0RZdU9oODV0dnhlUlE9PSIsInZhbHVlIjoiQ3U4MjVjMGtSOURnRVJsN3BnRElCTnEzQlRiNit4VG8wNzZPTTlFVVZnTnlocUFlNG1CZGhEeFQzTnBPcHVFY1kvMEx2WFZBYkFMN3ZyYzcvQk9TQm1xdWpFT2tBTkZta2VFVHZ0ekJuS1pCbndvQUtwVDhBTWZYeEVXTzcyaG95eXMzamhkNHhpT2kvT1ZGWlpMZkl6NTBUdW5qSEJpVVA2Zk8zVXRaeWhjOEROOXNkWFRZWnc2cjhhNHk4cU5iM05xRjY5bEQxREVxVCtKNzBORzFxZz09IiwibWFjIjoiYTE0YzA4YzAwYzE4YWE0MjFhZTliMjE2MmJhODJmNDU5MDI3ZmRjY2JmMWYxNWE2NjhhMzhjOTkwYTRlNThjMyIsInRhZyI6IiJ9',
                'X-Tenant': 1,
                'X-Client': 1,
                'X-Component': 1,
                'X-Domaine': 1,
                'X-Produit-Domaine': 1,
              },
            }
          : undefined,
      )
      .then(({ url }: { url: string }) => {
        window.open(url, '_blank', 'noopener');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [idFactureToEncaisseDirect, regie?.logicielsFacturier]);

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        columnGap: '1em',
      }}
    >
      <Grid as={Segment} stackable style={{ marginTop: 0, width: 500 }}>
        <Grid.Row>
          <Grid.Column>
            <Button
              icon="add"
              basic
              content="Création de factures"
              loading={loading}
              onClick={handleCreateFactures}
              fluid
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column>
            <Button
              icon="undo"
              basic
              content="Mise à jour des cycles de facturation"
              loading={loading}
              onClick={handleUpdateCycle}
              fluid
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column>
            <Button
              icon="remove"
              basic
              content="Suppression de toutes les factures"
              loading={loading}
              onClick={handleDeleteFactures}
              fluid
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column>
            <Button
              icon="remove"
              basic
              content="Suppression de tous les reçus"
              loading={loading}
              onClick={handleDeleteRecus}
              fluid
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column>
            {_.isNil(selectedFile) ? (
              <Form.Field>
                <label>Sélectionner un fichier de flux à importer :</label>
                <input type="file" onChange={handleFileChange} />
              </Form.Field>
            ) : (
              `Un fichier a été sélectionné : ${selectedFile.name}`
            )}
            <br />
            <Button
              attached="top"
              icon="upload"
              basic
              content="Test d'incorporation de flux"
              loading={loading}
              onClick={handleImportFlux}
              fluid
            />
            <Button
              attached="bottom"
              icon="upload"
              basic
              content="Test d'incorporation de contrats"
              loading={loading}
              onClick={handleImportFluxContrat}
              fluid
            />
            <br />
            <InputNumber
              label="id facture à encaisser "
              maxLength={999}
              disabled={false}
              style={{ width: 150 }}
              onChange={(val) => {
                setIdFactureToEncaisseDirect(val ? parseInt(val, 10) : null);
              }}
              data-testid="encaissefacturedirect"
              value={idFactureToEncaisseDirect}
            />
            <Button
              basic
              content="Encaissement direct"
              loading={loading}
              disabled={_.isNil(idFactureToEncaisseDirect)}
              onClick={handleEncaissementDirect}
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column>
            <Button
              icon="undo"
              basic
              content="Repasser cette régie en régie test"
              loading={loading}
              onClick={handleRegieEcole}
              fluid
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column>
            <Button
              icon="users"
              basic
              content="Activer la suppression des payeurs"
              loading={loading}
              onClick={handlePayeurDelete}
              fluid
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
      {full && (
        <Grid as={Segment} style={{ marginTop: 0, width: 500 }}>
          <Grid.Row>
            <Grid.Column>
              <ParametrageQualiteSuppressionRegie />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      )}
    </div>
  );
};

export default ParametrageQualite;
